import type { ReactNode } from 'react';
import { Children, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FlameIcon, HardHatIcon, ShieldIcon, WarningTriangleIcon, TagIcon, ChevronRightIcon } from '@utils/icons';
import { formatShortDate, currentLang, APP_DEFAULT_TZ } from '@utils/dateUtils';
import { fetchSafetyCompliance } from '@apis/safety-compliance';
import type { IExpiringCertificationRow, IHighRiskActivityRow } from '@interfaces/ISafetyCompliance';
import { SafetyShimmer } from '../DashboardSkeleton';
import { DashboardCard } from './_shared/DashboardCard';
import styles from './safetyComplianceCard.module.css';

/** Icon + colour per high-risk activity kind. Lookup tables (over inline
 *  ternaries) keep the mapping in one place and force exhaustiveness if a new
 *  kind is added to the union — matching the codebase pattern (e.g. RISK_CLASS
 *  in WeatherImpactCard). */
const ACTIVITY_ICON: Record<IHighRiskActivityRow['kind'], ReactNode> = {
  'hot-works': <FlameIcon />,
  'working-at-height': <HardHatIcon />,
  other: <WarningTriangleIcon />,
};

const ACTIVITY_ICON_CLASS: Record<IHighRiskActivityRow['kind'], string> = {
  'hot-works': styles.iconHot ?? '',
  'working-at-height': styles.iconPpe ?? '',
  other: styles.iconOther ?? '',
};

/** Each list section caps at this many rows before a show-more toggle. */
const COLLAPSE_AFTER = 4;

/** Shared chrome for the card's three list sections: title, empty state, and a
 *  list that caps at COLLAPSE_AFTER rows with a show-more / show-less toggle so
 *  a long section can't make the card scroll endlessly. */
function ComplianceSection({
  title,
  isEmpty,
  emptyLabel,
  children,
}: {
  title: ReactNode;
  isEmpty: boolean;
  emptyLabel: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const items = Children.toArray(children);
  const overflow = items.length - COLLAPSE_AFTER;
  const visible = expanded ? items : items.slice(0, COLLAPSE_AFTER);

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {isEmpty ? (
        <p className={styles.empty}>{emptyLabel}</p>
      ) : (
        <>
          <ul className={styles.list}>{visible}</ul>
          {overflow > 0 && (
            <button
              type="button"
              className={styles.showMore}
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={expanded}
            >
              {expanded
                ? t('dashboard.safetyCompliance.showLess')
                : t('dashboard.safetyCompliance.showMore', { count: overflow })}
            </button>
          )}
        </>
      )}
    </section>
  );
}

/** "Renewal due {date}" badge, coloured by overdue/soon. */
function CertDueBadge({ status, expirationDate }: { status: IExpiringCertificationRow['status']; expirationDate: string }) {
  const { t } = useTranslation();
  return (
    <span className={`${styles.certDue} ${status === 'overdue' ? styles.certOverdue : styles.certSoon}`}>
      {t('dashboard.safetyCompliance.renewalDue', {
        date: formatShortDate(expirationDate, currentLang(), APP_DEFAULT_TZ),
      })}
    </span>
  );
}

interface CertGroupData {
  certName: string;
  rows: IExpiringCertificationRow[];
  /** Worst status across the group's workers. */
  status: IExpiringCertificationRow['status'];
}

/** Group expiring certs by certificate so multiple workers holding the same
 *  soon-to-expire cert collapse into one expandable entry. Input is already
 *  sorted by date (soonest first), so each group's first row is its soonest. */
function groupCerts(rows: IExpiringCertificationRow[]): CertGroupData[] {
  const byName = new Map<string, IExpiringCertificationRow[]>();
  for (const row of rows) {
    const existing = byName.get(row.certName);
    if (existing) existing.push(row);
    else byName.set(row.certName, [row]);
  }
  return [...byName.values()].map((groupRows) => ({
    certName: groupRows[0]!.certName,
    rows: groupRows,
    status: groupRows.some((r) => r.status === 'overdue') ? 'overdue' : 'soon',
  }));
}

/** One certificate. A single holder renders as a plain row; multiple holders
 *  render as a collapsible group (collapsed by default) showing each worker. */
function CertGroup({ group }: { group: CertGroupData }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const soonest = group.rows[0]!;

  if (group.rows.length === 1) {
    return (
      <li className={styles.certRow}>
        <div className={styles.certMain}>
          <span className={styles.certNameText} title={group.certName}>{group.certName}</span>
          <CertDueBadge status={soonest.status} expirationDate={soonest.expirationDate} />
        </div>
        <span className={styles.certPerson} title={soonest.person}>{soonest.person}</span>
      </li>
    );
  }

  return (
    <li className={styles.certGroup}>
      <button
        type="button"
        className={styles.certGroupHeader}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <span className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`} aria-hidden>
          <ChevronRightIcon size={12} />
        </span>
        <span className={styles.certNameText} title={group.certName}>{group.certName}</span>
        <span className={styles.certGroupCount}>
          {t('dashboard.safetyCompliance.workerCount', { count: group.rows.length })}
        </span>
        <CertDueBadge status={group.status} expirationDate={soonest.expirationDate} />
      </button>
      {open && (
        <ul className={styles.certGroupList}>
          {group.rows.map((row) => (
            <li key={row.id} className={styles.certGroupRow}>
              <span className={styles.certPerson} title={row.person}>{row.person}</span>
              <CertDueBadge status={row.status} expirationDate={row.expirationDate} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function SafetyComplianceCard() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['safety-compliance', siteId],
    queryFn: ({ signal }) => fetchSafetyCompliance(siteId!, signal),
    enabled: Boolean(siteId),
  });

  return (
    <DashboardCard
      title={t('dashboard.safetyCompliance.title')}
      titleIcon={<ShieldIcon size={16} />}
      bodyClassName={styles.body}
    >
      {isLoading ? (
        <SafetyShimmer />
      ) : isError || !data ? (
        <div className={styles.stateMsg}>{t('dashboard.safetyCompliance.loadFailed')}</div>
      ) : (
        <>
          <div className={styles.headline}>
            <span className={styles.headlineValue}>{data.openIssuesCount}</span>
            <span className={styles.headlineLabel}>{t('dashboard.safetyCompliance.openSafetyIssues')}</span>
          </div>

          <div className={styles.tiles}>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>{t('dashboard.safetyCompliance.totalSubmitted')}</span>
              <span className={styles.tileValue}>{data.totalSubmitted}</span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>{t('dashboard.safetyCompliance.avgCloseTime')}</span>
              <span className={styles.tileValue}>
                {data.avgCloseDays === null
                  ? '—'
                  : t('dashboard.safetyCompliance.avgCloseValue', { days: data.avgCloseDays })}
              </span>
            </div>
          </div>

          <ComplianceSection
            title={t('dashboard.safetyCompliance.openSafetyIssues')}
            isEmpty={data.openIssues.length === 0}
            emptyLabel={t('dashboard.safetyCompliance.noOpenIssues')}
          >
            {data.openIssues.map((row) => (
              <li key={row.id} className={styles.issueRow}>
                <span className={styles.issueTitle}>{row.title}</span>
                <span className={styles.issueLoc}>{row.location}</span>
                <span
                  className={`${styles.dot} ${row.severity === 'high' ? styles.dotHigh : styles.dotMed}`}
                  aria-hidden
                />
              </li>
            ))}
          </ComplianceSection>

          <ComplianceSection
            title={
              <>
                <WarningTriangleIcon size={13} /> {t('dashboard.safetyCompliance.ongoingHighRisk')}
              </>
            }
            isEmpty={data.highRiskActivities.length === 0}
            emptyLabel={t('dashboard.safetyCompliance.noHighRisk')}
          >
            {data.highRiskActivities.map((row) => (
              <li key={row.id} className={styles.activityRow}>
                <span className={`${styles.activityIcon} ${ACTIVITY_ICON_CLASS[row.kind]}`}>
                  {ACTIVITY_ICON[row.kind]}
                </span>
                <span className={styles.activityLabel}>{row.label}</span>
                <span className={styles.activityLoc}>{row.location}</span>
              </li>
            ))}
          </ComplianceSection>

          <ComplianceSection
            title={
              <>
                <TagIcon size={13} /> {t('dashboard.safetyCompliance.certificationsExpiring')}
              </>
            }
            isEmpty={data.expiringCertifications.length === 0}
            emptyLabel={t('dashboard.safetyCompliance.noExpiringCerts')}
          >
            {groupCerts(data.expiringCertifications).map((group) => (
              <CertGroup key={group.certName} group={group} />
            ))}
          </ComplianceSection>
        </>
      )}
    </DashboardCard>
  );
}
